"use client";

import { useEffect, useState } from "react";
import { GroupSelector } from "@/components/group-selector";
import { QuestionTitle } from "@/components/ui/question-title";
import { TypeformShell } from "@/components/ui/typeform-shell";
import { sortGroups } from "@/lib/sort-groups";
import type { Group } from "@/lib/types";
import { CheckInForm } from "./check-in-form";
import { WelcomeScreen } from "./welcome-screen";

// Pre-selected via ?group= URL param — skip selector entirely.
interface RegisterFlowWithGroup {
  groupId: string;
  groupName: string | null;
  groups?: never;
}

// No URL param — show the group selector.
interface RegisterFlowWithSelector {
  groupId?: never;
  groupName?: never;
  groups: Group[];
}

type RegisterFlowProps = RegisterFlowWithGroup | RegisterFlowWithSelector;

export function RegisterFlow(props: RegisterFlowProps) {
  const [showWelcome, setShowWelcome] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [groups, setGroups] = useState<Group[]>(props.groups ?? []);

  useEffect(() => {
    fetch("/api/check-in").catch(() => undefined);
  }, []);

  // If geolocation is available, re-fetch groups sorted by distance.
  useEffect(() => {
    if (!(props.groups && navigator.geolocation)) {
      return;
    }
    const controller = new AbortController();
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;
        fetch(`/api/groups?lat=${latitude}&lon=${longitude}`, {
          signal: controller.signal,
        })
          .then((r) => r.json())
          .then((data: Group[]) => {
            if (Array.isArray(data)) {
              setGroups(sortGroups(data));
            }
          })
          .catch(() => {
            // Geolocation fetch failed or aborted — keep the server-fetched list.
          });
      },
      () => {
        // Permission denied or unavailable — keep the server-fetched list.
      }
    );
    return () => controller.abort();
  }, [props.groups]);

  if (showWelcome) {
    return <WelcomeScreen onContinue={() => setShowWelcome(false)} />;
  }

  const entering = {
    animation: "slide-up-in 0.45s ease-out both",
    position: "fixed" as const,
    inset: 0,
  };

  // Mode A: group pre-selected from URL param — render check-in directly.
  if (props.groupId) {
    return (
      <div style={entering}>
        <TypeformShell>
          <QuestionTitle>{props.groupName ?? "Check In"}</QuestionTitle>
          <CheckInForm groupId={props.groupId} preserveGroupParam />
        </TypeformShell>
      </div>
    );
  }

  // Mode B: group chosen via selector — group stays in state only, no UUID in URL.
  if (selectedGroup) {
    return (
      <div style={entering}>
        <TypeformShell onBack={() => setSelectedGroup(null)}>
          <QuestionTitle>{selectedGroup.name}</QuestionTitle>
          <CheckInForm groupId={selectedGroup.id} />
        </TypeformShell>
      </div>
    );
  }

  return (
    <div style={entering}>
      <TypeformShell scrollLayout>
        <GroupSelector
          groups={groups}
          onSelect={setSelectedGroup}
          title="Which group are you joining today?"
        />
      </TypeformShell>
    </div>
  );
}
